import {
  Button,
  Col,
  ConfigProvider,
  ConfirmModal,
  Empty,
  MoreButton,
  Row,
  Text,
  Timeline,
  theme,
} from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { useMessage } from '@/providers';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';

import {
  useBulkUpsertAndDeleteStages,
  useWorkflow,
} from '@/features/workflow/hooks/workflow';
import { WorkflowTypeLabelModifier } from '@/features/workflow/models/workflow';
import { useWorkflowStackSiderView } from '../../../../hooks/stack-sider-view';
import { StageStatusSignifier } from './StageStatusSignifier';
import { StageStep } from './StageStep';
import { StartWorkflowButton } from './StartWorkflowButton';

export const StageList: FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const stackSiderView = useWorkflowStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(
    siderConfig?.viewType === 'workflow-details',
    t('unreachable-code'),
  );

  const workflowId = siderConfig.id;

  const [isDeleteStagesOpenModal, setIsDeleteStagesOpenModal] = useState(false);

  const handleCloseModal = () => {
    setIsDeleteStagesOpenModal(false);
  };

  const { openMessage } = useMessage();
  const me = useMe();
  const { data: workflow } = useWorkflow(workflowId);

  const { mutateAsync: bulkUpsertAndDeleteStages } =
    useBulkUpsertAndDeleteStages();

  const handleDeleteStages = async () => {
    await bulkUpsertAndDeleteStages({
      stages: [],
      workflowType: undefined,
      id: siderConfig.id,
    });

    openMessage('success', t('message-delete-completed'));
    handleCloseModal();
  };

  if (!workflow || !me.data) {
    return null;
  }

  const isAuthorOrAssignee =
    me.data.id === workflow.authorId ||
    workflow.assigneeIds.includes(me.data.id);

  if (!workflow.stages.length) {
    return (
      <Row gutter={[0, 8]}>
        <Empty description={t('no-stage')}>
          {isAuthorOrAssignee && (
            <Button
              type="primary"
              onClick={() => {
                stackSiderView.push({
                  payload: {
                    config: {
                      ...siderConfig,
                      stageViewType: 'new-stages-form',
                    },
                  },
                  target: stackSiderView.target,
                });
              }}
            >
              {t('create')}
            </Button>
          )}
        </Empty>
      </Row>
    );
  }

  return (
    <>
      <Row gutter={[8, 0]}>
        <Col flex={1}>
          <Text
            variant="h5"
            text={t('workflow.workflow-with-workflow-type', {
              value: WorkflowTypeLabelModifier[workflow.workflowType],
            })}
          />
        </Col>
        {isAuthorOrAssignee && workflow.status === 'pending' && (
          <Col>
            <StartWorkflowButton workflowId={workflowId} />
          </Col>
        )}

        <Col>
          {isAuthorOrAssignee && (
            <MoreButton
              items={[
                {
                  key: 'update',
                  label: t('edit'),
                  disabled:
                    workflow.status === 'approved' ||
                    workflow.status === 'rejected',
                },
                {
                  key: 'delete',
                  label: t('delete'),
                  danger: true,
                  disabled: workflow.status === 'reviewing',
                },
              ]}
              onClick={({ key }) => {
                switch (key) {
                  case 'update':
                    stackSiderView.push({
                      payload: {
                        config: {
                          ...siderConfig,
                          stageViewType: 'edit-stages-form',
                        },
                      },
                      target: stackSiderView.target,
                    });
                    break;
                  case 'delete':
                    setIsDeleteStagesOpenModal(true);
                    break;
                }
              }}
            />
          )}
        </Col>

        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Timeline: {
                  tailColor:
                    workflow.workflowType === 'open'
                      ? 'transparent'
                      : token.colorBorder,
                },
              },
            }}
          >
            <Timeline
              style={{ paddingTop: 16 }}
              items={workflow.stages.map((stage) => {
                return {
                  dot: <StageStatusSignifier status={stage.status} />,
                  children: (
                    <StageStep
                      stage={stage}
                      hasAssignee={workflow.assignees.some(
                        (assignee) => assignee.id === me.data.id,
                      )}
                    />
                  ),
                };
              })}
            />
          </ConfigProvider>
        </Col>
      </Row>

      <ConfirmModal
        open={isDeleteStagesOpenModal}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: t('workflow'),
        })}
        onOk={handleDeleteStages}
        onCancel={handleCloseModal}
      />
    </>
  );
};
